/**
 * StewardChatPane — chat-style transcript of recent steward activity.
 *
 * Renders an "activity timeline" (queue items + handoffs + escalations
 * unified) as a scrollable list of compact rows. Each row carries a
 * left-side icon for the activity kind and a right-side timestamp.
 *
 * Notion-strict: no bubble chrome, no avatars, hairline divider, hover
 * bg-muted/40.
 */
import type { DepartmentActivity } from '@chaos/types/department';
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowUpToLine,
  CheckCircle2,
  ListChecks,
} from 'lucide-react';

import { cn } from '../../lib/utils';
import { fmtRelative } from './kpi-tile';

interface StewardChatPaneProps {
  activity: readonly DepartmentActivity[];
  emptyHint?: string;
}

const KIND_ICON: Record<DepartmentActivity['kind'], typeof ListChecks> = {
  queue_item: ListChecks,
  handoff_in: ArrowDownToLine,
  handoff_out: ArrowUpToLine,
  escalation_opened: AlertTriangle,
  escalation_resolved: CheckCircle2,
};

const KIND_LABEL: Record<DepartmentActivity['kind'], string> = {
  queue_item: 'Queue',
  handoff_in: 'Handoff in',
  handoff_out: 'Handoff out',
  escalation_opened: 'Escalation',
  escalation_resolved: 'Resolved',
};

const KIND_TONE: Record<DepartmentActivity['kind'], string> = {
  queue_item: 'text-muted-foreground',
  handoff_in: 'text-foreground',
  handoff_out: 'text-muted-foreground',
  escalation_opened: 'text-destructive',
  escalation_resolved: 'text-success',
};

export function StewardChatPane({
  activity,
  emptyHint = 'No activity yet.',
}: StewardChatPaneProps) {
  if (activity.length === 0) {
    return <p className="px-2 py-3 text-[12px] text-muted-foreground/70">{emptyHint}</p>;
  }
  return (
    <ol className="flex flex-col divide-y divide-border/70">
      {activity.map((row) => {
        const Icon = KIND_ICON[row.kind];
        return (
          <li
            key={`${row.kind}:${row.id}`}
            className="group/row flex items-start gap-3 px-2 py-2 transition-colors hover:bg-muted/40"
          >
            <span
              className={cn(
                'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center',
                KIND_TONE[row.kind],
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden />
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="flex items-center gap-2 text-[12px]">
                <span className="font-medium text-foreground">{KIND_LABEL[row.kind]}</span>
                <span className="text-muted-foreground">·</span>
                <span className="truncate font-normal text-foreground">{row.title}</span>
              </div>
              {row.detail ? (
                <span className="line-clamp-2 text-[11px] text-muted-foreground">{row.detail}</span>
              ) : null}
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
                {row.status}
              </span>
            </div>
            <span className="shrink-0 text-[11px] text-muted-foreground/80" title={row.occurredAt}>
              {fmtRelative(row.occurredAt)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
